/**
 * Server timestamp normalization.
 *
 * The .NET backend reads ProcessExecution / ProcessThreadExecution datetime columns as
 * `DateTime` with `Kind=Unspecified`, so System.Text.Json serializes them with no offset
 * marker: "2026-08-01T15:10:15.376". The digits are UTC, but ECMAScript parses an
 * offset-less date-*time* string as LOCAL time (only date-only forms default to UTC).
 * So `new Date(raw)` silently shifts the instant by the viewer's UTC offset — a run that
 * started at 8:40 PM IST rendered as "3:10 PM", exactly UTC+5:30 out.
 *
 * Tagging the string as UTC before it reaches any consumer fixes every downstream
 * `toLocaleString()` / duration calculation at once, which is why this belongs here at the
 * wire→domain boundary rather than at each render site.
 *
 * SignalR payloads already carry a trailing "Z" (the execution hub uses a different
 * serialization path), and the guard leaves those untouched — so this is safe to apply to
 * any server-supplied timestamp regardless of transport.
 */

/** Matches a trailing "Z"/"z" or a numeric UTC offset such as "+05:30" / "-0800". */
const HAS_TIMEZONE = /[Zz]$|[+-]\d{2}:?\d{2}$/;

/**
 * Tags an offset-less server timestamp as UTC so `new Date()` parses the correct instant.
 * Strings that already declare a timezone, and empty/absent values, are returned unchanged.
 */
export function normalizeServerDate(raw: string): string;
export function normalizeServerDate(raw: string | null | undefined): string | undefined;
export function normalizeServerDate(raw: string | null | undefined): string | undefined {
  if (!raw) return raw ?? undefined;
  return HAS_TIMEZONE.test(raw) ? raw : `${raw}Z`;
}
